import React, { useState, useEffect, useRef, useCallback } from 'react';
import { theme } from '../theme';
import MonacoSQLEditor from './MonacoSQLEditor';
import ResultsTable from './ResultsTable';
import SddChipsAndWarnings from './SddChipsAndWarnings';
import { Button, Select, Spinner } from './ui';
import Icons from './Icons';
import { formatSQL } from '../utils/formatSQL';
import { useConfig } from '../context/ConfigContext';
// ---------------------------------------------------------------------------
// Split Pane — draggable divider between SQL editor and results
// ---------------------------------------------------------------------------
export default function SplitPane({ activeTabId, sql, setSql, runQuery, loading, validating, results, error, maxRows, onMaxRowsChange, savedSelection, onSelectionPersist, schema, connections, tabConnectionId, onTabConnectionChange, onClearQuery, aiPrompt, aiRetrying, sddContext, sddWarnings, onRetryAi, onDismissSdd }) {
  const ActiveEditor = MonacoSQLEditor;
  const { connectionStatuses } = useConfig();
  const containerRef = useRef(null);
  const [editorHeight, setEditorHeight] = useState(180);
  const [dragging, setDragging] = useState(false);

  // Track selected SQL for run-selected feature
  const editorRef = useRef(null);
  const selectedSqlRef = useRef('');
  const selectionRangeRef = useRef({ start: 0, end: 0 });
  const [hasSelection, setHasSelection] = useState(false);
  const handleSelectionChange = useCallback(({ text, start, end }) => {
    const hasTextSelection = !!text && start !== end;
    if (hasTextSelection) {
      onSelectionPersist?.({ text, start, end });
    } else {
      onSelectionPersist?.(null);
    }
    selectedSqlRef.current = hasTextSelection ? text : '';
    selectionRangeRef.current = { start, end };
    setHasSelection(hasTextSelection);
  }, [onSelectionPersist]);

  // Restore each tab's last editor selection/cursor when switching tabs.
  // Do not depend on savedSelection here; live selection changes should not
  // trigger restore while the user is actively selecting text.
  useEffect(() => {
    const saved = savedSelection;
    if (!saved) {
      selectedSqlRef.current = '';
      selectionRangeRef.current = { start: 0, end: 0 };
      setHasSelection(false);
      setTimeout(() => {
        editorRef.current?.clearSelection?.();
      }, 0);
      return;
    }
    selectedSqlRef.current = saved.text || '';
    selectionRangeRef.current = { start: saved.start ?? 0, end: saved.end ?? 0 };
    setHasSelection(!!saved.text);
    setTimeout(() => {
      editorRef.current?.restoreSelection(saved.start ?? 0, saved.end ?? 0);
    }, 0);
  }, [activeTabId]);
  const handleRun = useCallback((selFromKeyboard = null) => {
    const sel = selFromKeyboard ?? selectedSqlRef.current;
    runQuery(sel || null);
    // Restore the selection highlight so user can see which statement ran
    if (sel) {
      const { start, end } = selectionRangeRef.current;
      setTimeout(() => editorRef.current?.restoreSelection(start, end), 0);
    }
  }, [runQuery]);
  const handleFormat = useCallback(() => {
    setSql(formatSQL(typeof sql === 'string' ? sql : ''));
  }, [setSql, sql]);

  // Normalize format shortcut handling across editor engines.
  useEffect(() => {
    const onKeyDown = (e) => {
      const isFormatShortcut = (e.ctrlKey || e.metaKey) && e.shiftKey && e.key?.toLowerCase() === 'f';
      if (!isFormatShortcut) return;

      const root = containerRef.current;
      const target = e.target;
      if (!root || !(target instanceof Node) || !root.contains(target)) return;

      e.preventDefault();
      e.stopPropagation();
      handleFormat();
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [handleFormat]);

  const activeConn   = connections?.find(c => c.id === tabConnectionId);
  const connBaseUrl  = activeConn?.baseUrl  || '';
  const connUsername = activeConn?.username || '';
  const connPassword = activeConn?.password || '';
  // Use the shared test result from ConfigContext so all indicators stay in sync
  const connStatus  = tabConnectionId ? (connectionStatuses[tabConnectionId] ?? null) : null;
  const isConnected = !!(connBaseUrl && connUsername);

  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);

  const rowLimitOptions = [
    { value: '50', label: '50 rows' },
    { value: '100', label: '100 rows' },
    { value: '200', label: '200 rows' },
    { value: '500', label: '500 rows' },
    { value: '1000', label: '1,000 rows' },
    { value: '5000', label: '5,000 rows' },
    { value: '0', label: 'No limit' },
  ];

  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e) => {
      const delta = e.clientY - dragStartY.current;
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;
      const maxH = containerRect.height - 160;
      const newHeight = Math.max(80, Math.min(maxH, dragStartHeight.current + delta));
      setEditorHeight(newHeight);
    };

    const handleMouseUp = () => {
      setDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  const handleDragStart = (e) => {
    e.preventDefault();
    dragStartY.current = e.clientY;
    dragStartHeight.current = editorHeight;
    setDragging(true);
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div ref={containerRef} style={{
      flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      padding: '12px 16px',
    }}>
      {/* SDD context banner — shown on AI-generated tabs (esp. after Retry with AI) */}
      {(sddContext?.length > 0 || sddWarnings?.length > 0) && (
        <div style={{ flexShrink: 0, marginBottom: '6px' }}>
          <SddChipsAndWarnings sddContext={sddContext} sddWarnings={sddWarnings} onDismiss={onDismissSdd} compact />
        </div>
      )}

      {/* SQL Editor — fixed height controlled by drag */}
      <div style={{ height: `${editorHeight}px`, flexShrink: 0, display: 'flex', minHeight: '80px', position: 'relative', zIndex: 2 }}>
        <ActiveEditor
          ref={editorRef}
          modelPath={activeTabId ? `tab-${activeTabId}.sql` : 'tab.sql'}
          value={sql}
          onChange={setSql}
          onRun={handleRun}
          onFormat={handleFormat}
          onSelectionChange={handleSelectionChange}
          schema={schema}
          disabled={!isConnected}
        />
        {/* Clear button — floats over the top-right corner of the editor */}
        {sql && (
          <button
            onClick={() => onClearQuery ? onClearQuery() : setSql('')}
            title="Clear editor"
            style={{
              position: 'absolute', top: '6px', right: '22px', zIndex: 10,
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '3px 8px', borderRadius: '5px', border: 'none',
              background: 'rgba(0,0,0,0.45)', color: '#fff',
              fontSize: '11px', fontWeight: 500, cursor: 'pointer',
              opacity: 0.7, transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = 1}
            onMouseLeave={e => e.currentTarget.style.opacity = 0.7}
          >
            <Icons.X style={{ width: 10, height: 10 }} />
            Clear
          </button>
        )}
      </div>

      {/* Toolbar: Run Query + Format + Limit Rows — fixed between editor and results */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '8px 0', flexShrink: 0, position: 'relative', zIndex: 2,
      }}>
        <Button onClick={() => handleRun()} disabled={loading || validating || !isConnected}
          title={!isConnected ? 'Add a connection first' : undefined}
          icon={(loading || validating) ? <Spinner /> : <Icons.Play />}>
          {validating ? 'Validating...' : loading ? 'Running...' : hasSelection ? 'Run Selected' : 'Run Query'}
        </Button>
        <Button variant="outline" size="md" icon={<Icons.Format />} onClick={handleFormat}>
          Format
        </Button>
        <Select value={maxRows} onChange={onMaxRowsChange} options={rowLimitOptions} />

        {/* Per-tab connection selector with status indicator */}
        {connections && connections.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
            <span style={{ fontSize: '11px', color: theme.textMuted, whiteSpace: 'nowrap' }}>Connection:</span>
            {/* Status dot — mirrors the shared connection test result */}
            <div style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              background: connStatus === 'ok'      ? theme.success
                        : connStatus === 'fail'    ? theme.danger
                        : connStatus === 'testing' ? theme.warning
                        : theme.border,
              boxShadow: connStatus === 'ok'   ? `0 0 6px ${theme.success}99`
                       : connStatus === 'fail' ? `0 0 6px ${theme.danger}99`
                       : 'none',
              transition: 'background 0.3s, box-shadow 0.3s',
            }} />
            <select
              value={tabConnectionId || ''}
              onChange={(e) => onTabConnectionChange(e.target.value)}
              style={{
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                color: theme.text,
                fontSize: '12px',
                fontWeight: 600,
                padding: '4px 8px',
                cursor: 'pointer',
                outline: 'none',
                maxWidth: '160px',
              }}
            >
              {connections.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <span style={{ fontSize: '11px', color: theme.textMuted, whiteSpace: 'nowrap' }}>
          {validating ? 'Validating SQL…' : loading ? 'Executing…'
            : !isConnected ? 'Add a connection first'
            : 'Ctrl+Enter to run'}
        </span>
      </div>

      {/* Draggable divider */}
      <div
        onMouseDown={handleDragStart}
        style={{
          height: '9px', flexShrink: 0, cursor: 'row-resize',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{
          width: '40px', height: '3px', borderRadius: '2px',
          background: dragging ? theme.accent : theme.border,
          transition: dragging ? 'none' : 'background 0.15s',
        }} />
      </div>

      {/* Results — takes remaining space, fully contained */}
      <div style={{
        flex: 1, overflow: 'hidden',
        display: 'flex', flexDirection: 'column', minHeight: 0,
        position: 'relative', zIndex: 1,
      }}>
        <ResultsTable
          columns={results.columns} rows={results.rows} rawXml={results.rawXml}
          error={error} loading={loading} validating={validating}
          isConnected={isConnected}
          canRetryAi={!!aiPrompt}
          onRetryAi={onRetryAi}
          aiRetrying={aiRetrying}
        />
      </div>
    </div>
  );
}
